import { collect, flushCollectQueue } from '@/modules/Collect';
import { useChatStore } from '@/stores/chat';
import { usePreviewStore } from '@/stores/preview';
import { useProjectStore } from '@/stores/project';
import {
  LONG_TASK_REPORT_THRESHOLD_MS,
  LONG_TASK_REPORT_THROTTLE_MS,
} from './const';

interface RuntimeCounters {
  longTaskCount: number;
  longTaskTotalMs: number;
  longTaskMaxMs: number;
}

const counters: RuntimeCounters = {
  longTaskCount: 0,
  longTaskTotalMs: 0,
  longTaskMaxMs: 0,
};

let initialized = false;
let longTaskObserver: PerformanceObserver | null = null;
let lastLongTaskReportAt = 0;

function getDeviceMemory(): number | null {
  const value = (navigator as Navigator & { deviceMemory?: number }).deviceMemory;
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function getConnectionInfo() {
  const connection = (navigator as Navigator & {
    connection?: {
      effectiveType?: string;
      rtt?: number;
      downlink?: number;
      saveData?: boolean;
    };
  }).connection;

  return {
    effectiveType: connection?.effectiveType ?? null,
    rttMs: typeof connection?.rtt === 'number' ? connection.rtt : null,
    downlinkMbps: typeof connection?.downlink === 'number' ? connection.downlink : null,
    saveData: connection?.saveData ?? null,
  };
}

function getJsHeapSnapshot() {
  const memory = performance.memory;
  if (!memory) {
    return {
      available: false,
      usedBytes: null,
      totalBytes: null,
      limitBytes: null,
      utilizationPct: null,
    };
  }

  const usedBytes = memory.usedJSHeapSize;
  const limitBytes = memory.jsHeapSizeLimit;
  return {
    available: true,
    usedBytes,
    totalBytes: memory.totalJSHeapSize,
    limitBytes,
    utilizationPct: limitBytes > 0 ? Math.round((usedBytes / limitBytes) * 10_000) / 100 : null,
  };
}

function getPreviewSnapshot() {
  const preview = usePreviewStore.getState();
  const livePluginStatus = preview.previewDebugInfo.live.pluginLoadStatus;
  const chat = useChatStore.getState();

  return {
    projectId: useProjectStore.getState().projectId || null,
    targetLayer: preview.targetLayer,
    iframeLoadId: preview.iframeLoadId || null,
    sandboxRefreshNonce: preview.sandboxRefreshNonce,
    livePreviewStatus: preview.livePreviewStatus,
    sandboxRuntimeStatus: preview.sandboxRuntimeStatus,
    sandboxRuntimeConnectionStatus: preview.sandboxRuntimeConnectionStatus,
    sandboxContentReady: preview.sandboxContentReady,
    sandboxIframeReady: preview.sandboxIframeReady,
    livePreviewProblem: preview.livePreviewProblem ?? null,
    previewPageHasError: preview.pageStatus.hasError,
    preReleaseReady: preview.preReleaseReady,
    preReleaseIframeReady: preview.preReleaseIframeReady,
    liveBridgeStatus: preview.previewDebugInfo.live.bridgeStatus,
    livePluginStatus: livePluginStatus?.status ?? null,
    livePluginCount: livePluginStatus?.plugins?.length ?? null,
    livePluginErrorCount: livePluginStatus?.plugins?.filter(plugin => plugin.status === 'error').length ?? null,
    isStreaming: chat.isStreaming,
    chatMessageCount: Array.isArray(chat.messages) ? chat.messages.length : null,
  };
}

function getResourceSnapshot() {
  const entries = performance.getEntriesByType('resource') as PerformanceResourceTiming[];
  let transferBytes = 0;
  let decodedBytes = 0;
  let imageResourceCount = 0;
  let imageTransferBytes = 0;
  let imageDecodedBytes = 0;
  entries.forEach((entry) => {
    const transfer = Number(entry.transferSize) || 0;
    const decoded = Number(entry.decodedBodySize) || 0;
    transferBytes += transfer;
    decodedBytes += decoded;
    if (entry.initiatorType === 'img' || entry.initiatorType === 'image') {
      imageResourceCount += 1;
      imageTransferBytes += transfer;
      imageDecodedBytes += decoded;
    }
  });
  const images = [...document.images];
  const estimatedImageBitmapBytes = images.reduce(
    (sum, image) => sum + Math.max(0, image.naturalWidth * image.naturalHeight * 4),
    0,
  );
  const largestImageBitmapBytes = images.reduce(
    (largest, image) => Math.max(largest, image.naturalWidth * image.naturalHeight * 4),
    0,
  );
  const estimatedCanvasBytes = [...document.getElementsByTagName('canvas')].reduce(
    (sum, canvas) => sum + Math.max(0, canvas.width * canvas.height * 4),
    0,
  );
  return {
    resourceCount: entries.length,
    imageResourceCount,
    resourceTransferBytes: transferBytes,
    resourceDecodedBytes: decodedBytes,
    imageTransferBytes,
    imageDecodedBytes,
    estimatedImageBitmapBytes,
    largestImageBitmapBytes,
    estimatedCanvasBytes,
  };
}

/**
 * 轻量核心快照：仅读内存中已算好的计数器 + store 状态 + performance.memory。
 * 不做全 DOM 遍历、不扫 resource timing，可安全用于高频事件驱动的上报。
 */
function getCoreSnapshot() {
  const jsHeap = getJsHeapSnapshot();
  const wasDiscarded = (document as Document & { wasDiscarded?: boolean }).wasDiscarded ?? false;

  return {
    documentVisibilityState: document.visibilityState,
    documentWasDiscarded: wasDiscarded,
    jsHeapAvailable: jsHeap.available,
    jsHeapUsedBytes: jsHeap.usedBytes,
    jsHeapTotalBytes: jsHeap.totalBytes,
    jsHeapLimitBytes: jsHeap.limitBytes,
    jsHeapUtilizationPct: jsHeap.utilizationPct,
    longTaskCount: counters.longTaskCount,
    longTaskTotalMs: Math.round(counters.longTaskTotalMs),
    longTaskMaxMs: Math.round(counters.longTaskMaxMs),
    ...getPreviewSnapshot(),
  };
}

/**
 * 完整快照：在核心快照基础上附加"重"字段（全 DOM 遍历 + resource 扫描）
 * 与静态/慢变字段（设备 / 网络）。仅供低频 init 使用。
 */
function getFullSnapshot() {
  const connection = getConnectionInfo();
  const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;

  const snapshot = {
    ...getCoreSnapshot(),
    navigationType: navigation?.type ?? null,
    deviceMemoryGb: getDeviceMemory(),
    hardwareConcurrency: navigator.hardwareConcurrency || null,
    connectionEffectiveType: connection.effectiveType,
    connectionRttMs: connection.rttMs,
    connectionDownlinkMbps: connection.downlinkMbps,
    connectionSaveData: connection.saveData,
    domNodes: document.getElementsByTagName('*').length,
    domIframes: document.getElementsByTagName('iframe').length,
    domImages: document.getElementsByTagName('img').length,
    domCanvases: document.getElementsByTagName('canvas').length,
    domScripts: document.scripts.length,
    ...getResourceSnapshot(),
  };

  return snapshot;
}

/** 保留完整快照的具名导出（供调试用）。 */
export function getRuntimeDiagnosticsSnapshot() {
  return getFullSnapshot();
}

export function reportRuntimeDiagnostic(
  reason: string,
  extra: Record<string, unknown> = {},
  options: { full?: boolean } = {},
): void {
  collect('runtime_diagnostic', {
    reason,
    // init 走完整快照；long_task 走轻量核心快照，避免在卡顿回调里做全 DOM/resource 扫描。
    ...(options.full ? getFullSnapshot() : getCoreSnapshot()),
    ...extra,
  });
}

function flushOnVisibilityChange() {
  if (document.visibilityState === 'hidden') {
    flushCollectQueue();
  }
}

function flushOnPageHide() {
  flushCollectQueue();
}

function flushOnFreeze() {
  flushCollectQueue();
}

export function initRuntimeDiagnostics(): void {
  if (initialized) {
    return;
  }
  initialized = true;

  if (typeof PerformanceObserver !== 'undefined') {
    try {
      longTaskObserver = new PerformanceObserver((list) => {
        list.getEntries().forEach((entry) => {
          counters.longTaskCount += 1;
          counters.longTaskTotalMs += entry.duration;
          counters.longTaskMaxMs = Math.max(counters.longTaskMaxMs, entry.duration);
          if (
            entry.duration >= LONG_TASK_REPORT_THRESHOLD_MS
            && Date.now() - lastLongTaskReportAt >= LONG_TASK_REPORT_THROTTLE_MS
          ) {
            lastLongTaskReportAt = Date.now();
            reportRuntimeDiagnostic('long_task', {
              longTaskDurationMs: Math.round(entry.duration),
              longTaskEntryName: entry.name,
            });
          }
        });
      });
      longTaskObserver.observe({ entryTypes: ['longtask'] });
    } catch {
      longTaskObserver = null;
    }
  }

  document.addEventListener('visibilitychange', flushOnVisibilityChange);
  window.addEventListener('pagehide', flushOnPageHide);
  document.addEventListener('freeze', flushOnFreeze);
  reportRuntimeDiagnostic('init', {}, { full: true });
}

export function destroyRuntimeDiagnostics(): void {
  if (!initialized) {
    return;
  }
  initialized = false;
  longTaskObserver?.disconnect();
  longTaskObserver = null;
  document.removeEventListener('visibilitychange', flushOnVisibilityChange);
  window.removeEventListener('pagehide', flushOnPageHide);
  document.removeEventListener('freeze', flushOnFreeze);
}
